import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { GoogleMap, Marker, InfoWindow } from "@react-google-maps/api";
import Swal from "sweetalert2";
import { FiClock, FiMapPin, FiArrowRight, FiRefreshCw, FiCheck, FiChevronDown, FiChevronUp, FiChevronLeft, FiChevronRight, FiTruck, FiTrash2, FiGitMerge, FiDatabase } from "react-icons/fi";
import { RiMotorbikeFill } from "react-icons/ri";

import { Col, Row } from "react-bootstrap";
import moment from "moment";
import { BrComerceApiService } from "../../../../../services/BrComerceApiService";
import { Loading } from "../../../../../components/Loading";
import DatePicker from "../../../../../components/Input/DatePicker";
import AutocompleteInput from "../../../components/AutoCompletInput";
import InputSelectAsync from "../../../../../components/InputSelectAsync";

import { ZoneSnapshotModal } from "./ZoneSnapshotModal";
import styles from "./styles.module.scss";

const CLUSTER_COLORS = [
  "#E53935", "#1E88E5", "#43A047", "#FB8C00",
  "#8E24AA", "#00ACC1", "#6D4C41", "#D81B60",
  "#00897B", "#FDD835"
];

const mapContainerStyle = { width: "100%", height: "100%" };
const defaultCenter = { lat: -22.9035, lng: -43.2096 };

const STATUS_LABELS = {
  draft: { label: "Rascunho", className: "statusDraft" },
  partial: { label: "Parcial", className: "statusPartial" },
  completed: { label: "Concluído", className: "statusCompleted" },
};

const getStatusInfo = (status) => STATUS_LABELS[status] || { label: status, className: "statusDraft" };

const hasDeliveryFailure = (ship) => Boolean(ship?.hasDeliveryFailure);

const ZoneMultiSelect = ({ selected, onChange, zoneOptions }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const toggle = (value) => {
    onChange((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value]
    );
  };

  const displayLabel = (val) => {
    if (val === "__no_zone__") return "Sem zona";
    return val;
  };

  const label = selected.length === 0
    ? "Todas as zonas"
    : selected.length <= 2
      ? selected.map(displayLabel).join(", ")
      : `${selected.length} zonas`;

  return (
    <div className={styles.filterItemSmall} style={{ flex: "1 1 180px", minWidth: 150, position: "relative" }} ref={ref}>
      <div className={styles.input} onClick={() => setOpen((p) => !p)} style={{ cursor: "pointer", userSelect: "none" }}>
        <span style={{ fontSize: 13, color: selected.length ? "#4d4c4d" : "#807e80", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>
          {label}
        </span>
        <FiChevronDown size={14} style={{ marginLeft: "auto", flexShrink: 0 }} />
      </div>
      {open && (
        <div className={styles.zoneDropdown}>
          {selected.length > 0 && (
            <div className={styles.zoneDropdownClear} onClick={() => onChange([])}>
              Limpar seleção
            </div>
          )}
          <label className={styles.zoneDropdownItem}>
            <input
              type="checkbox"
              checked={selected.includes("__no_zone__")}
              onChange={() => toggle("__no_zone__")}
            />
            <span style={{ fontStyle: "italic" }}>Sem zona definida</span>
          </label>
          {zoneOptions.map((item) => (
            <label key={item.value} className={styles.zoneDropdownItem}>
              <input
                type="checkbox"
                checked={selected.includes(item.value)}
                onChange={() => toggle(item.value)}
              />
              <span>{item.label}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

const RouteSuggestion = () => {
  const api = new BrComerceApiService();

  const isLoaded = !!window.google?.maps;

  const [loading, setLoading] = useState(false);
  const [dateRange, setDateRange] = useState(null);
  const [clusterRadiusKm, setClusterRadiusKm] = useState("");
  const [maxStopsPerRoute, setMaxStopsPerRoute] = useState("");
  const [suggestions, setSuggestions] = useState(null);
  const [activeCluster, setActiveCluster] = useState(null);
  const [selectedDrivers, setSelectedDrivers] = useState({});
  const [infoWindowData, setInfoWindowData] = useState(null);
  const [mapCenter, setMapCenter] = useState(defaultCenter);

  const [clusterAddresses, setClusterAddresses] = useState({});
  const [clusterCollectsEnabled, setClusterCollectsEnabled] = useState({});
  const [movingItem, setMovingItem] = useState(null); // { type, id, fromCluster }
  const [batches, setBatches] = useState([]);
  const [showBatchList, setShowBatchList] = useState(false);
  const [batchPage, setBatchPage] = useState(0);
  const [slideDir, setSlideDir] = useState(null); // "left" | "right"
  const [expandedClusters, setExpandedClusters] = useState({}); // { clusterId: true }
  const [vehicleFilter, setVehicleFilter] = useState("all"); // "all" | "moto" | "caminhao"
  const [maxLengthMoto, setMaxLengthMoto] = useState("");
  const [maxWidthMoto, setMaxWidthMoto] = useState("");
  const [maxHeightMoto, setMaxHeightMoto] = useState("");
  const [maxWeightMoto, setMaxWeightMoto] = useState("");
  const [zoneFilter, setZoneFilter] = useState([]);
  const [zoneOptions, setZoneOptions] = useState([]);
  const [searchedDrivers, setSearchedDrivers] = useState({});
  const [availableCollects, setAvailableCollects] = useState([]);
  const [showAvailableCollects, setShowAvailableCollects] = useState(false);
  const [selectedCollect, setSelectedCollect] = useState(null);
  const [mergingCluster, setMergingCluster] = useState(null); // clusterId being merged from
  const [showSnapshotModal, setShowSnapshotModal] = useState(false);
  const mapRef = useRef(null);

  const onMapLoad = useCallback((map) => {
    mapRef.current = map;
  }, []);

  useEffect(() => {
    api.makeHttpRequest({ url: "/route-suggestion-admin/zones" })
      .then((res) => {
        if (res?.zones) {
          const options = res.zones.map((name) => ({
            value: name.split(" ")[0],
            label: name,
          }));
          setZoneOptions(options);
        }
      })
      .catch(() => {});
  }, []);

  const handleDateChange = useCallback((dates) => {
    setDateRange(dates);
  }, []);

  const initFromResponse = useCallback((response) => {
    setSuggestions(response);

    if (response?.suggestions?.length) {
      const firstClusterId = response.suggestions[0].clusterId;
      setActiveCluster(firstClusterId);

      const first = response.suggestions[0].cluster?.centroid;
      const newCenter = first?.lat ? { lat: first.lat, lng: first.lng } : defaultCenter;
      setMapCenter(newCenter);
      if (mapRef.current) mapRef.current.panTo(newCenter);

      const drivers = {};
      const collectsEnabled = {};
      response.suggestions.forEach((s) => {
        const cid = s.clusterId;
        if (s.selectedDriverId) {
          const found = s.rankedDrivers?.find(rd => rd?.driver?.id === s.selectedDriverId || rd?.driver?.sub === s.selectedDriverId);
          if (found) drivers[cid] = found;
        } else if (s.rankedDrivers?.length) {
          drivers[cid] = s.rankedDrivers[0];
        }
        collectsEnabled[cid] = true;
      });
      setSelectedDrivers(drivers);
      setClusterCollectsEnabled(collectsEnabled);
    }
  }, []);

  const loadBatch = useCallback(async (batchId) => {
    setLoading(true);
    setSuggestions(null);
    setActiveCluster(null);
    setSelectedDrivers({});
    setSearchedDrivers({});
    setClusterAddresses({});
    setClusterCollectsEnabled({});
    setMovingItem(null);
    setVehicleFilter("all");

    try {
      const response = await api.makeHttpRequest({
        method: "GET",
        url: `/route-suggestion-admin/batches/${batchId}`,
      });
      initFromResponse(response);
      loadAvailableCollects(batchId);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível carregar a geração.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [initFromResponse, loadAvailableCollects]);

  const loadBatches = useCallback(async () => {
    try {
      const response = await api.makeHttpRequest({
        method: "GET",
        url: "/route-suggestion-admin/batches",
      });
      setBatches(response || []);
    } catch (err) {
      // silent
    }
  }, []);

  const loadAvailableCollects = useCallback(async (batchId) => {
    try {
      const response = await api.makeHttpRequest({
        method: "GET",
        url: `/route-suggestion-admin/batches/${batchId}/available-own-collects`,
      });
      setAvailableCollects(response || []);
    } catch (err) {
      // silent
    }
  }, []);

  useEffect(() => {
    window.setPageTitle("Sugestão de Rotas");
    loadBatches();
  }, []);

  const handleGenerate = useCallback(async () => {
    setLoading(true);
    setSuggestions(null);
    setActiveCluster(null);
    setSelectedDrivers({});
    setSearchedDrivers({});
    setClusterAddresses({});
    setClusterCollectsEnabled({});
    setMovingItem(null);
    setVehicleFilter("all");

    const data = {
      maxStopsPerRoute: maxStopsPerRoute !== "" ? Number(maxStopsPerRoute) : 30,
      clusterRadiusKm: clusterRadiusKm !== "" ? Number(clusterRadiusKm) : 50,
      maxLengthMoto: maxLengthMoto !== "" ? Number(maxLengthMoto) : 0,
      maxWidthMoto: maxWidthMoto !== "" ? Number(maxWidthMoto) : 0,
      maxHeightMoto: maxHeightMoto !== "" ? Number(maxHeightMoto) : 0,
      maxWeightMoto: maxWeightMoto !== "" ? Number(maxWeightMoto) : 0,
    };
    if (zoneFilter.length > 0) data.zones = zoneFilter;

    if (dateRange?.dateStart && dateRange?.dateEnd) {
      data.startDate = moment(dateRange.dateStart, "DD/MM/YYYY").toDate();
      data.endDate = moment(dateRange.dateEnd, "DD/MM/YYYY").toDate();
    }

    try {
      const response = await api.makeHttpRequest({
        method: "POST",
        url: "/route-suggestion-admin/generate",
        data,
      });

      initFromResponse(response);
      loadBatches();
      if (response?.batchId) loadAvailableCollects(response.batchId);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível gerar sugestões.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [dateRange, maxStopsPerRoute, clusterRadiusKm, maxLengthMoto, maxWidthMoto, maxHeightMoto, maxWeightMoto, zoneFilter, initFromResponse, loadBatches, loadAvailableCollects]);

  const handleAddressChange = useCallback((clusterId, field, event) => {
    const { value } = event.target;
    setClusterAddresses(prev => ({
      ...prev,
      [clusterId]: {
        ...prev[clusterId],
        [field]: {
          ...(prev[clusterId]?.[field] || {}),
          value,
          error: !value || value.length < 3 ? "Campo obrigatório" : "",
          isOK: !value || value.length < 3 ? "notOk" : "vazio",
        },
      },
    }));
  }, []);

  const setAddressState = useCallback((clusterId, field) => {
    return (updater) => {
      setClusterAddresses(prev => {
        const current = prev[clusterId]?.[field] || { value: "", error: "", latLong: { lat: "", lng: "" }, isOK: "vazio" };
        const updated = typeof updater === "function" ? updater(current) : updater;
        return {
          ...prev,
          [clusterId]: {
            ...prev[clusterId],
            [field]: updated,
          },
        };
      });
    };
  }, []);

  const handleMoveItem = useCallback(async (itemId, toClusterId) => {
    if (!suggestions?.batchId) return;
    setMovingItem(null);
    setLoading(true);

    try {
      await api.makeHttpRequest({
        method: "PATCH",
        url: `/route-suggestion-admin/batches/${suggestions.batchId}/move`,
        data: { itemId, toClusterId },
      });

      await loadBatch(suggestions.batchId);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível mover o item.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
      setLoading(false);
    }
  }, [suggestions?.batchId, loadBatch]);

  const handleAccept = useCallback(async (clusterId) => {
    const suggestion = suggestions?.suggestions?.find(s => s.clusterId === clusterId);
    if (!suggestion) return;

    const driver = selectedDrivers[clusterId];
    const addresses = clusterAddresses[clusterId];

    if (!driver) {
      Swal.fire({ icon: "warning", title: "Atenção", text: "Selecione um motorista." });
      return;
    }

    if (!addresses?.origin?.latLong?.lat || !addresses?.destination?.latLong?.lat) {
      Swal.fire({ icon: "warning", title: "Atenção", text: "Preencha os endereços de origem e destino." });
      return;
    }

    setLoading(true);

    try {
      await api.makeHttpRequest({
        method: "POST",
        url: `/route-suggestion-admin/batches/${suggestions.batchId}/clusters/${clusterId}/accept`,
        data: {
          idDriver: driver?.driver?.id ?? driver?.driver?.sub,
          originDestination: {
            origin: {
              latitude: addresses.origin.latLong.lat,
              longitude: addresses.origin.latLong.lng,
              name: "",
              address: addresses.origin.value,
            },
            destination: {
              latitude: addresses.destination.latLong.lat,
              longitude: addresses.destination.latLong.lng,
              name: "",
              address: addresses.destination.value,
            },
          },
          type: "delivery",
        },
      });

      Swal.fire({
        icon: "success",
        title: "Sucesso",
        text: `Rota criada para ${driver?.driver?.name || "motorista"} com ${suggestion.cluster?.totalStops || 0} paradas.`,
      });

      await loadBatch(suggestions.batchId);
      loadBatches();
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível criar a rota.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
      setLoading(false);
    }
  }, [suggestions, selectedDrivers, clusterAddresses, loadBatch, loadBatches]);

  const handleDeleteBatch = useCallback(async (batchId) => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Remover geração?",
      text: "Esta ação não pode ser desfeita.",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!result.value) return;

    setLoading(true);
    try {
      await api.makeHttpRequest({
        method: "DELETE",
        url: `/route-suggestion-admin/batches/${batchId}`,
      });

      if (suggestions?.batchId === batchId) {
        setSuggestions(null);
        setActiveCluster(null);
        setSelectedDrivers({});
        setSearchedDrivers({});
        setClusterAddresses({});
        setClusterCollectsEnabled({});
      }

      await loadBatches();
      Swal.fire({ icon: "success", title: "Removido", text: "Geração removida com sucesso.", timer: 1500, showConfirmButton: false });
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível remover a geração.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
    }
  }, [suggestions?.batchId, loadBatches]);

  const handleRemoveItem = useCallback(async (itemId) => {
    if (!suggestions?.batchId) return;

    const result = await Swal.fire({
      icon: "warning",
      title: "Remover item?",
      text: "O item será removido desta sugestão de rota.",
      showCancelButton: true,
      confirmButtonText: "Sim, remover",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!result.value) return;

    setLoading(true);
    try {
      await api.makeHttpRequest({
        method: "DELETE",
        url: `/route-suggestion-admin/batches/${suggestions.batchId}/items/${itemId}`,
      });

      await loadBatch(suggestions.batchId);
      loadAvailableCollects(suggestions.batchId);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível remover o item.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
      setLoading(false);
    }
  }, [suggestions?.batchId, loadBatch, loadAvailableCollects]);

  const handleAddCollectToCluster = useCallback(async (ownCollectId, clusterId) => {
    if (!suggestions?.batchId) return;

    setLoading(true);
    try {
      await api.makeHttpRequest({
        method: "POST",
        url: `/route-suggestion-admin/batches/${suggestions.batchId}/clusters/${clusterId}/items`,
        data: { ownCollectId },
      });

      setSelectedCollect(null);
      await loadBatch(suggestions.batchId);
      loadAvailableCollects(suggestions.batchId);
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível adicionar a coleta.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
      setLoading(false);
    }
  }, [suggestions?.batchId, loadBatch, loadAvailableCollects]);

  const handleClearAllBatches = useCallback(async () => {
    const result = await Swal.fire({
      icon: "warning",
      title: "Limpar todas as gerações?",
      text: "Todas as gerações serão removidas. Esta ação não pode ser desfeita.",
      showCancelButton: true,
      confirmButtonText: "Sim, limpar todas",
      cancelButtonText: "Cancelar",
      confirmButtonColor: "#d33",
    });

    if (!result.value) return;

    setLoading(true);
    try {
      await Promise.all(batches.map((b) =>
        api.makeHttpRequest({ method: "DELETE", url: `/route-suggestion-admin/batches/${b.id}` })
      ));

      setSuggestions(null);
      setActiveCluster(null);
      setSelectedDrivers({});
      setSearchedDrivers({});
      setClusterAddresses({});
      setClusterCollectsEnabled({});
      setBatches([]);
      Swal.fire({ icon: "success", title: "Limpo", text: "Todas as gerações foram removidas.", timer: 1500, showConfirmButton: false });
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível limpar as gerações.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
    } finally {
      setLoading(false);
      loadBatches();
    }
  }, [batches, loadBatches]);

  const handleMergeClusters = useCallback(async (fromClusterId, toClusterId) => {
    if (!suggestions?.batchId || !suggestions?.suggestions) return;
    setMergingCluster(null);
    setLoading(true);

    try {
      const fromSuggestion = suggestions.suggestions.find((s) => s.clusterId === fromClusterId);
      if (!fromSuggestion) throw new Error("Cluster de origem não encontrado.");

      const allItems = [
        ...(fromSuggestion.cluster?.shippings || []).map((s) => s.itemId),
        ...(fromSuggestion.cluster?.ownCollects || []).map((oc) => oc.itemId),
      ].filter(Boolean);

      for (const itemId of allItems) {
        await api.makeHttpRequest({
          method: "PATCH",
          url: `/route-suggestion-admin/batches/${suggestions.batchId}/move`,
          data: { itemId, toClusterId },
        });
      }

      await loadBatch(suggestions.batchId);
      Swal.fire({ icon: "success", title: "Mesclado", text: "Rotas mescladas com sucesso.", timer: 1500, showConfirmButton: false });
    } catch (err) {
      const errorMsg = err?.response?.data?.message || "Não foi possível mesclar as rotas.";
      Swal.fire({ icon: "error", title: "Erro", text: errorMsg });
      setLoading(false);
    }
  }, [suggestions?.batchId, suggestions?.suggestions, loadBatch]);

  const markers = useMemo(() => {
    if (!suggestions?.suggestions) return [];

    const items = [];
    suggestions.suggestions.forEach((s, idx) => {
      const color = CLUSTER_COLORS[idx % CLUSTER_COLORS.length];
      const clusterId = s.clusterId;

      (s.cluster?.shippings || []).forEach((ship) => {
        if (!ship.location?.lat || !ship.location?.lng) return;
        items.push({
          key: `ship-${ship.id}`,
          position: { lat: ship.location.lat, lng: ship.location.lng },
          color,
          clusterId,
          label: ship.sro,
          title: `${ship.sro} - ${ship.name || ""}`,
          type: "shipping",
          data: ship,
        });
      });

      (s.cluster?.ownCollects || []).forEach((oc) => {
        if (!oc.location?.lat || !oc.location?.lng) return;
        items.push({
          key: `oc-${oc.id}`,
          position: { lat: oc.location.lat, lng: oc.location.lng },
          color,
          clusterId,
          label: `Coleta #${oc.id}`,
          title: `Coleta #${oc.id} - ${oc.collectAddressCity || ""}`,
          type: "ownCollect",
          data: oc,
        });
      });
    });

    return items;
  }, [suggestions]);

  const formatTime = (dateStr) => {
    if (!dateStr) return "";
    const d = new Date(dateStr);
    return d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit", timeZone: "America/Sao_Paulo" });
  };

  return (
    <>
      <ZoneSnapshotModal open={showSnapshotModal} onClose={() => setShowSnapshotModal(false)} />
      <Loading loading={loading} />
      <Row className={styles.container}>
        <Col className={`col ${styles.leftContainer}`}>
          <div className={styles.header}>
            <h1 className={styles.title}>Sugestão de Rotas</h1>
          </div>

        <div className={styles.filtersRow}>
          <div className={styles.filterItemDate}>
            <div className={styles.calendarInput}>
              <DatePicker
                placeholder="Período"
                change={handleDateChange}
                range
              />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={1} max={100} placeholder="Paradas (30)"
                value={maxStopsPerRoute} onChange={(e) => setMaxStopsPerRoute(e.target.value)} />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={1} max={50} placeholder="Raio km (50)"
                value={clusterRadiusKm} onChange={(e) => setClusterRadiusKm(e.target.value)} />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={0} placeholder="Comp. moto (0)"
                value={maxLengthMoto} onChange={(e) => setMaxLengthMoto(e.target.value)} />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={0} placeholder="Larg. moto (0)"
                value={maxWidthMoto} onChange={(e) => setMaxWidthMoto(e.target.value)} />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={0} placeholder="Alt. moto (0)"
                value={maxHeightMoto} onChange={(e) => setMaxHeightMoto(e.target.value)} />
            </div>
          </div>
          <div className={styles.filterItemSmall}>
            <div className={styles.input}>
              <input type="number" min={0} step="0.1" placeholder="Peso moto (0)"
                value={maxWeightMoto} onChange={(e) => setMaxWeightMoto(e.target.value)} />
            </div>
          </div>
          <ZoneMultiSelect selected={zoneFilter} onChange={setZoneFilter} zoneOptions={zoneOptions} />
          <button
            className={styles.generateBtn}
            onClick={handleGenerate}
            disabled={loading}
          >
            Gerar Sugestões
          </button>
          <button
            className={styles.snapshotBtn}
            onClick={() => setShowSnapshotModal(true)}
            title="Gerenciar snapshots de zonas"
          >
            <FiDatabase size={14} />
            Zonas
          </button>
        </div>

        {batches.length > 0 && (
          <div className={styles.batchListSection}>
            <div className={styles.batchListHeader}>
              <div
                className={styles.batchListToggle}
                onClick={() => setShowBatchList(prev => !prev)}
              >
                <FiRefreshCw size={14} />
                <span>Sugestões recentes ({batches.length})</span>
                {showBatchList ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
              </div>
              {batches.length > 1 && (
                <button
                  className={styles.clearAllBtn}
                  onClick={(e) => {
                    e.stopPropagation();
                    handleClearAllBatches();
                  }}
                >
                  <FiTrash2 size={12} />
                  Limpar todas
                </button>
              )}
            </div>

            {(() => {
              const perPage = 6;
              const totalPages = Math.ceil(batches.length / perPage);
              const page = Math.min(batchPage, totalPages - 1);
              const visible = batches.slice(page * perPage, (page + 1) * perPage);

              return (
                <div className={`${styles.batchCarousel} ${showBatchList ? styles.batchCarouselOpen : styles.batchCarouselClosed}`}>
                  <div
                    className={`${styles.batchCarouselContent}${slideDir === "left" ? ` ${styles.slideLeft}` : ""}${slideDir === "right" ? ` ${styles.slideRight}` : ""}`}
                    onAnimationEnd={() => setSlideDir(null)}
                  >
                    {visible.map((b) => {
                      const statusInfo = getStatusInfo(b.status);
                      const isCurrent = suggestions?.batchId === b.id;
                      const progress = b.totalClusters > 0
                        ? Math.round((b.acceptedClusters / b.totalClusters) * 100)
                        : 0;

                      return (
                        <div
                          key={b.id}
                          className={`${styles.batchCard} ${isCurrent ? styles.batchCardActive : ""}`}
                          onClick={() => {
                            loadBatch(b.id);
                            setShowBatchList(false);
                          }}
                        >
                          <div className={styles.batchCardHeader}>
                            <span className={styles.batchCardDate}>
                              {moment(b.createdAt).subtract(3, "hours").format("DD/MM/YYYY [às] HH:mm")}
                            </span>
                            <div className={styles.batchCardActions}>
                              <span className={`${styles.batchStatusBadge} ${styles[statusInfo.className]}`}>
                                {statusInfo.label}
                              </span>
                              <button
                                className={styles.batchDeleteBtn}
                                title="Remover geração"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleDeleteBatch(b.id);
                                }}
                              >
                                <FiTrash2 size={14} />
                              </button>
                            </div>
                          </div>
                          <div className={styles.batchCardBody}>
                            <div className={styles.batchCardStat}>
                              <span className={styles.batchCardStatValue}>{b.totalClusters}</span>
                              <span className={styles.batchCardStatLabel}>
                                {b.totalClusters === 1 ? "rota sugerida" : "rotas sugeridas"}
                              </span>
                            </div>
                            <div className={styles.batchCardStat}>
                              <span className={styles.batchCardStatValue}>{b.acceptedClusters}</span>
                              <span className={styles.batchCardStatLabel}>
                                {b.acceptedClusters === 1 ? "aceita" : "aceitas"}
                              </span>
                            </div>
                          </div>
                          {b.totalClusters > 0 && (
                            <div className={styles.batchProgress}>
                              <div className={styles.batchProgressBar}>
                                <div
                                  className={styles.batchProgressFill}
                                  style={{ width: `${progress}%` }}
                                />
                              </div>
                              <span className={styles.batchProgressLabel}>{progress}%</span>
                            </div>
                          )}
                          {isCurrent && (
                            <div className={styles.batchCurrentTag}>
                              <FiCheck size={12} /> Visualizando
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>

                  {totalPages > 1 && (
                    <div className={styles.batchPagination}>
                      <button
                        className={styles.batchPageBtn}
                        disabled={page === 0}
                        onClick={() => { setSlideDir("right"); setBatchPage(p => Math.max(0, p - 1)); }}
                      >
                        <FiChevronLeft size={16} />
                      </button>
                      <span className={styles.batchPageInfo}>{page + 1} / {totalPages}</span>
                      <button
                        className={styles.batchPageBtn}
                        disabled={page >= totalPages - 1}
                        onClick={() => { setSlideDir("left"); setBatchPage(p => Math.min(totalPages - 1, p + 1)); }}
                      >
                        <FiChevronRight size={16} />
                      </button>
                    </div>
                  )}
                </div>
              );
            })()}
          </div>
        )}

        {suggestions && (
          <div className={styles.summary}>
            <div className={styles.summaryCard}>
              <p className={styles.summaryValue}>{suggestions.totalShippingsAnalyzed}</p>
              <p className={styles.summaryLabel}>Entregas analisadas</p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryValue}>{suggestions.totalClustersGenerated}</p>
              <p className={styles.summaryLabel}>Rotas sugeridas</p>
            </div>
            <div className={styles.summaryCard}>
              <p className={styles.summaryValue}>{suggestions.unclusteredShippings}</p>
              <p className={styles.summaryLabel}>Não agrupadas</p>
            </div>
          </div>
        )}

        {suggestions?.suggestions?.length > 0 && (
          <div className={styles.vehicleFilterRow}>
            <span className={styles.vehicleFilterLabel}>Filtrar por veículo:</span>
            {["all", "moto", "caminhao"].map((f) => (
              <button
                key={f}
                className={`${styles.vehicleFilterBtn} ${vehicleFilter === f ? styles.vehicleFilterActive : ""}`}
                onClick={() => setVehicleFilter(f)}
              >
                {f === "all" ? "Todos" : f === "moto" ? "Moto" : "Caminhão"}
              </button>
            ))}
          </div>
        )}

        {suggestions?.suggestions?.length > 0 && (
          <div className={styles.mainContent}>
            <div className={styles.mapContainer}>
              {isLoaded && (
                <GoogleMap
                  mapContainerStyle={mapContainerStyle}
                  center={mapCenter}
                  zoom={12}
                  onLoad={onMapLoad}
                  options={{
                    styles: [
                      { featureType: "poi", stylers: [{ visibility: "off" }] },
                      { featureType: "transit", stylers: [{ visibility: "off" }] },
                    ],
                  }}
                >
                  {markers
                    .filter((m) => activeCluster !== null && m.clusterId === activeCluster)
                    .map((m) => (
                    <Marker
                      key={m.key}
                      position={m.position}
                      title={m.title}
                      icon={window.google?.maps?.SymbolPath ? {
                        path: window.google.maps.SymbolPath.CIRCLE,
                        fillColor: m.color,
                        fillOpacity: 1,
                        strokeColor: "#fff",
                        strokeWeight: 2,
                        scale: 8,
                      } : undefined}
                      onClick={() => setInfoWindowData(m)}
                    />
                  ))}

                  {infoWindowData && (
                    <InfoWindow
                      position={infoWindowData.position}
                      onCloseClick={() => setInfoWindowData(null)}
                    >
                      <div>
                        <strong>{infoWindowData.label}</strong>
                        {infoWindowData.type === "shipping" && (
                          <>
                            <p style={{ margin: "4px 0 0", fontSize: 12 }}>
                              {infoWindowData.data.name}
                            </p>
                            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#807e80" }}>
                              {infoWindowData.data.destinationDistrict}, {infoWindowData.data.destinationCity}
                              {infoWindowData.data.zone && <span style={{ marginLeft: 4, padding: "1px 5px", background: "#e8f5e9", borderRadius: 4, fontSize: 10 }}>{infoWindowData.data.zone}</span>}
                            </p>
                          </>
                        )}
                        {infoWindowData.type === "ownCollect" && (
                          <>
                            {infoWindowData.data.collectAddressName && (
                              <p style={{ margin: "4px 0 0", fontSize: 12 }}>
                                {infoWindowData.data.collectAddressName}
                              </p>
                            )}
                            <p style={{ margin: "2px 0 0", fontSize: 11, color: "#807e80" }}>
                              {infoWindowData.data.collectAddressCity} - {infoWindowData.data.collectAddressState}
                            </p>
                          </>
                        )}
                      </div>
                    </InfoWindow>
                  )}
                </GoogleMap>
              )}
            </div>

            <div className={styles.cardsContainer}>
              {suggestions.suggestions
                .filter((s) => vehicleFilter === "all" || s.cluster?.vehicleType === vehicleFilter)
                .map((suggestion, idx) => {
                const cluster = suggestion.cluster;
                const cid = suggestion.clusterId;
                const color = CLUSTER_COLORS[idx % CLUSTER_COLORS.length];
                const isActive = activeCluster === cid;
                const isAccepted = suggestion.clusterStatus === "accepted";
                const driver = selectedDrivers[cid];
                const addresses = clusterAddresses[cid] || {};
                const originOK = addresses?.origin?.isOK === "ok";
                const destOK = addresses?.destination?.isOK === "ok";

                return (
                  <div
                    key={cid}
                    className={`${styles.clusterCard} ${isActive ? styles.activeCluster : ""}`}
                    onClick={() => {
                      if (mergingCluster && mergingCluster !== cid && !isAccepted) {
                        handleMergeClusters(mergingCluster, cid);
                        return;
                      }
                      if (selectedCollect && !isAccepted) {
                        handleAddCollectToCluster(selectedCollect.id, cid);
                        return;
                      }
                      setActiveCluster(cid);
                      setMovingItem(null);
                      setMergingCluster(null);
                      if (!cluster?.centroid?.lat) return;
                      const newCenter = { lat: cluster.centroid.lat, lng: cluster.centroid.lng };
                      setMapCenter(newCenter);
                      if (mapRef.current) mapRef.current.panTo(newCenter);
                    }}
                  >
                    {mergingCluster && mergingCluster !== cid && !isAccepted && (
                      <div
                        className={styles.mergeTarget}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleMergeClusters(mergingCluster, cid);
                        }}
                      >
                        <FiGitMerge size={14} />
                        Clique para mesclar aqui
                      </div>
                    )}

                    <div
                      className={styles.clusterHeader}
                      onClick={(e) => {
                        e.stopPropagation();
                        setExpandedClusters(prev => ({ ...prev, [cid]: !prev[cid] }));
                      }}
                    >
                      <div className={styles.clusterTitle}>
                        <div className={styles.colorDot} style={{ backgroundColor: color }} />
                        <h4>{cluster?.region || "Sem região"}</h4>
                        <span className={`${styles.vehicleBadge} ${cluster?.vehicleType === "caminhao" ? styles.vehicleCaminhao : styles.vehicleMoto}`}>
                          {cluster?.vehicleType === "caminhao" ? <FiTruck size={12} /> : <RiMotorbikeFill size={14} />}
                          {cluster?.vehicleType === "caminhao" ? "Caminhão" : "Moto"}
                        </span>
                      </div>
                      <div className={styles.clusterActions}>
                        <span className={styles.clusterStat}>{cluster?.totalStops || 0} paradas</span>
                        <span className={styles.clusterStat}>{cluster?.estimatedKms || 0} km</span>
                        {!isAccepted && suggestions.suggestions.length > 1 && (
                          <button
                            className={`${styles.mergeBtn} ${mergingCluster === cid ? styles.mergeBtnActive : ""}`}
                            title={mergingCluster === cid ? "Cancelar mesclagem" : "Mesclar com outra rota"}
                            onClick={(e) => {
                              e.stopPropagation();
                              setMergingCluster(prev => prev === cid ? null : cid);
                            }}
                          >
                            <FiGitMerge size={13} />
                          </button>
                        )}
                        {expandedClusters[cid] ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                      </div>
                    </div>

                    {!expandedClusters[cid] && (
                      <div className={styles.clusterSummary}>
                        <span>{(cluster?.shippings || []).length} entrega(s)</span>
                        {(cluster?.ownCollects || []).length > 0 && <span>{cluster.ownCollects.length} coleta(s)</span>}
                        <span>{(suggestion.rankedDrivers || []).length} motorista(s)</span>
                        {isAccepted && <span className={styles.acceptedBadge} style={{ margin: 0, padding: "2px 8px", fontSize: 11 }}>Rota criada</span>}
                      </div>
                    )}

                    <div className={`${styles.clusterBody} ${expandedClusters[cid] ? styles.clusterBodyOpen : styles.clusterBodyClosed}`}>

                    {cluster?.timeConstraints && (
                      <div className={styles.timeConstraint}>
                        <FiClock size={14} />
                        Janela: {formatTime(cluster.timeConstraints?.start)} - {formatTime(cluster.timeConstraints?.end)}
                      </div>
                    )}

                    <div className={styles.shippingsList}>
                      <p className={styles.sectionTitle}>
                        Entregas ({(cluster?.shippings || []).length})
                      </p>
                      {(cluster?.shippings || []).map((ship) => (
                        <div
                          key={ship.id}
                          className={`${styles.shippingItem} ${hasDeliveryFailure(ship) ? styles.shippingItemFailure : ""}`}
                          title={hasDeliveryFailure(ship) ? "Envio com falha de entrega anterior" : undefined}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!ship.location?.lat || !ship.location?.lng) return;
                            const point = { lat: ship.location.lat, lng: ship.location.lng };
                            setMapCenter(point);
                            if (mapRef.current) mapRef.current.panTo(point);
                            setActiveCluster(cid);
                          }}
                        >
                          <span className={styles.sro}>{ship.sro}</span>
                          <span className={styles.name}>{ship.name}</span>
                          <span className={styles.address}>{ship.destinationAddress}</span>
                          <span className={styles.district}>
                            {ship.destinationDistrict}
                            {ship.zone && <span className={styles.zoneBadge}>{ship.zone}</span>}
                          </span>
                          {!isAccepted && (
                            <div className={styles.itemActions}>
                              {suggestions.suggestions.length > 1 && (
                                <div className={styles.moveWrapper}>
                                  <button
                                    className={styles.moveBtn}
                                    title="Mover para outra rota"
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      setMovingItem(prev =>
                                        prev?.id === ship.id && prev?.type === "shipping"
                                          ? null
                                          : { type: "shipping", id: ship.id, fromCluster: cid }
                                      );
                                    }}
                                  >
                                    <FiArrowRight size={12} />
                                  </button>
                                  {movingItem?.type === "shipping" && movingItem?.id === ship.id && (
                                    <div className={styles.moveDropdown} onClick={(e) => e.stopPropagation()}>
                                      {suggestions.suggestions.map((s, tIdx) => {
                                        if (s.clusterId === cid) return null;
                                        return (
                                          <div
                                            key={s.clusterId}
                                            className={styles.moveOption}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleMoveItem(ship.itemId, s.clusterId);
                                            }}
                                          >
                                            <div className={styles.colorDot} style={{ backgroundColor: CLUSTER_COLORS[tIdx % CLUSTER_COLORS.length] }} />
                                            <span>{s.cluster?.region} ({s.cluster?.totalStops})</span>
                                          </div>
                                        );
                                      })}
                                    </div>
                                  )}
                                </div>
                              )}
                              <button
                                className={styles.removeItemBtn}
                                title="Remover item"
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleRemoveItem(ship.itemId);
                                }}
                              >
                                <FiTrash2 size={12} />
                              </button>
                            </div>
                          )}
                        </div>
                      ))}
                    </div>

                    {(cluster?.ownCollects || []).length > 0 && (
                      <div className={styles.shippingsList}>
                        <div className={styles.sectionTitleRow}>
                          <p className={styles.sectionTitle}>
                            Coletas Próprias ({(cluster?.ownCollects || []).length})
                          </p>
                          {!isAccepted && (
                            <label
                              className={styles.checkboxLabel}
                              onClick={(e) => e.stopPropagation()}
                            >
                              <input
                                type="checkbox"
                                checked={clusterCollectsEnabled[cid] !== false}
                                onChange={(e) => {
                                  setClusterCollectsEnabled(prev => ({
                                    ...prev,
                                    [cid]: e.target.checked,
                                  }));
                                }}
                              />
                              Incluir
                            </label>
                          )}
                        </div>
                        {clusterCollectsEnabled[cid] !== false && (cluster?.ownCollects || []).map((oc) => (
                          <div
                            key={oc.id}
                            className={styles.shippingItem}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (!oc.location?.lat || !oc.location?.lng) return;
                              const point = { lat: oc.location.lat, lng: oc.location.lng };
                              setMapCenter(point);
                              if (mapRef.current) mapRef.current.panTo(point);
                              setActiveCluster(cid);
                            }}
                          >
                            <span className={styles.sro}>#{oc.id}</span>
                            <span className={styles.name}>{oc.collectAddressName}</span>
                            <span className={styles.address}>{oc.collectAddressCity} - {oc.collectAddressState}</span>
                            {!isAccepted && (
                              <div className={styles.itemActions}>
                                {suggestions.suggestions.length > 1 && (
                                  <div className={styles.moveWrapper}>
                                    <button
                                      className={styles.moveBtn}
                                      title="Mover para outra rota"
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        setMovingItem(prev =>
                                          prev?.id === oc.id && prev?.type === "ownCollect"
                                            ? null
                                            : { type: "ownCollect", id: oc.id, fromCluster: cid }
                                        );
                                      }}
                                    >
                                      <FiArrowRight size={12} />
                                    </button>
                                    {movingItem?.type === "ownCollect" && movingItem?.id === oc.id && (
                                      <div className={styles.moveDropdown} onClick={(e) => e.stopPropagation()}>
                                        {suggestions.suggestions.map((s, tIdx) => {
                                          if (s.clusterId === cid) return null;
                                          return (
                                            <div
                                              key={s.clusterId}
                                              className={styles.moveOption}
                                              onClick={(e) => {
                                                e.stopPropagation();
                                                handleMoveItem(oc.itemId, s.clusterId);
                                              }}
                                            >
                                              <div className={styles.colorDot} style={{ backgroundColor: CLUSTER_COLORS[tIdx % CLUSTER_COLORS.length] }} />
                                              <span>{s.cluster?.region} ({s.cluster?.totalStops})</span>
                                            </div>
                                          );
                                        })}
                                      </div>
                                    )}
                                  </div>
                                )}
                                <button
                                  className={styles.removeItemBtn}
                                  title="Remover item"
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    handleRemoveItem(oc.itemId);
                                  }}
                                >
                                  <FiTrash2 size={12} />
                                </button>
                              </div>
                            )}
                          </div>
                        ))}
                      </div>
                    )}

                    <div className={styles.driversSection}>
                      <p className={styles.sectionTitle}>Motoristas sugeridos</p>
                      {(suggestion.rankedDrivers || []).map((rd, dIdx) => (
                        <div
                          key={rd?.driver?.sub || dIdx}
                          className={`${styles.driverOption} ${driver?.driver?.sub === rd?.driver?.sub ? styles.selectedDriver : ""}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            if (!isAccepted) {
                              setSelectedDrivers(prev => ({ ...prev, [cid]: rd }));
                              setSearchedDrivers(prev => ({ ...prev, [cid]: null }));
                            }
                          }}
                        >
                          <div className={styles.driverInfo}>
                            <span className={styles.driverName}>
                              {dIdx + 1}. {rd?.driver?.name || "Motorista"}
                            </span>
                            <span className={styles.driverDetails}>
                              {rd?.distanceToClusterKm ?? "-"} km {rd?.currentLocation ? "(GPS)" : "(base)"} | {rd?.activeRoutesToday ?? 0} rota(s) ativa(s)
                              {rd?.currentZone && <span className={styles.driverZoneBadge}>{rd.currentZone}</span>}
                            </span>
                          </div>
                          <div className={styles.driverScore}>
                            <span className={styles.score}>{rd?.score ?? "-"}</span>
                            <span className={styles.scoreLabel}>score</span>
                          </div>
                        </div>
                      ))}

                      {(suggestion.rankedDrivers || []).length === 0 && (
                        <p style={{ fontSize: 12, color: "#807e80" }}>Nenhum motorista sugerido</p>
                      )}

                      {!isAccepted && (
                        <div className={styles.driverSearchSection}>
                          <p className={styles.driverSearchLabel}>Ou buscar outro motorista:</p>
                          <div onClick={(e) => e.stopPropagation()}>
                            <InputSelectAsync
                              search={searchedDrivers[cid] || null}
                              setSearch={(val) => {
                                setSearchedDrivers(prev => ({ ...prev, [cid]: val }));
                                if (val) {
                                  setSelectedDrivers(prev => ({
                                    ...prev,
                                    [cid]: {
                                      driver: { id: val.value, sub: val.sub, name: val.label },
                                      score: null,
                                      distanceToClusterKm: null,
                                      activeRoutesToday: null,
                                    },
                                  }));
                                } else {
                                  setSelectedDrivers(prev => {
                                    const updated = { ...prev };
                                    delete updated[cid];
                                    return updated;
                                  });
                                }
                              }}
                              listUserOrDriver="driver"
                              placeholder="Buscar motorista por nome..."
                            />
                          </div>
                        </div>
                      )}
                    </div>

                    {!isAccepted && (
                      <div className={styles.addressSection}>
                        <p className={styles.sectionTitle}>Endereços da rota</p>
                        <div className={styles.addressInputs}>
                          <div
                            className={styles.addressInput}
                            role={addresses?.origin?.isOK || "vazio"}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AutocompleteInput
                              placeholder="Endereço de origem"
                              name="value"
                              value={addresses?.origin?.value || ""}
                              handleChange={(e) => handleAddressChange(cid, "origin", e)}
                              setState={setAddressState(cid, "origin")}
                            />
                          </div>
                          <div
                            className={styles.addressInput}
                            role={addresses?.destination?.isOK || "vazio"}
                            onClick={(e) => e.stopPropagation()}
                          >
                            <AutocompleteInput
                              placeholder="Endereço de destino"
                              name="value"
                              value={addresses?.destination?.value || ""}
                              handleChange={(e) => handleAddressChange(cid, "destination", e)}
                              setState={setAddressState(cid, "destination")}
                            />
                          </div>
                        </div>
                      </div>
                    )}

                    {isAccepted ? (
                      <div className={styles.acceptedBadge}>
                        Rota criada
                      </div>
                    ) : (
                      <button
                        className={styles.acceptBtn}
                        disabled={!driver || !originOK || !destOK || loading}
                        onClick={(e) => {
                          e.stopPropagation();
                          handleAccept(cid);
                        }}
                      >
                        Criar Rota
                      </button>
                    )}
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {suggestions && availableCollects.length > 0 && (
          <div className={styles.availableCollectsSection}>
            <div
              className={styles.batchListToggle}
              onClick={() => setShowAvailableCollects(prev => !prev)}
            >
              <FiMapPin size={14} />
              <span>Coletas disponíveis ({availableCollects.length})</span>
              {showAvailableCollects ? <FiChevronUp size={14} /> : <FiChevronDown size={14} />}
            </div>

            {selectedCollect && (
              <div className={styles.collectAssignHint}>
                Coleta #{selectedCollect.id} selecionada — clique em um cluster para adicioná-la
                <button
                  className={styles.cancelSelectBtn}
                  onClick={() => setSelectedCollect(null)}
                >
                  Cancelar
                </button>
              </div>
            )}

            {showAvailableCollects && (
              <div className={styles.availableCollectsGrid}>
                {availableCollects.map((oc) => {
                  const isSelected = selectedCollect?.id === oc.id;

                  return (
                    <div
                      key={oc.id}
                      className={`${styles.availableCollectCard} ${isSelected ? styles.availableCollectSelected : ""}`}
                      onClick={() => setSelectedCollect(isSelected ? null : oc)}
                    >
                      <div className={styles.availableCollectInfo}>
                        <span className={styles.availableCollectId}>#{oc.id}</span>
                        <span className={styles.availableCollectName}>{oc.collectAddressName}</span>
                        <span className={styles.availableCollectCity}>
                          {oc.collectAddressCity} - {oc.collectAddressState}
                        </span>
                      </div>
                      {isSelected && (
                        <span className={styles.availableCollectSelectedBadge}>Selecionada</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {suggestions && suggestions.suggestions?.length === 0 && (
          <div className={styles.emptyState}>
            <FiMapPin />
            <h3>Nenhuma rota sugerida</h3>
            <p>Não foram encontradas entregas elegíveis para agrupar neste período.</p>
          </div>
        )}

        {!suggestions && (
          <div className={styles.emptyState}>
            <FiMapPin />
            <h3>Sugestão de Rotas Inteligente</h3>
            <p>Selecione uma data e clique em "Gerar Sugestões" para agrupar entregas por proximidade e receber sugestões de motoristas.</p>
          </div>
        )}
        </Col>
      </Row>
    </>
  );
};

export { RouteSuggestion };
